import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Code, Eye, Zap, FlaskConical } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const capabilities = [
  {
    icon: Code,
    title: 'Internal Systems',
    description: "Internal systems tailored to you, built on our own infrastructure, engineered with precision.",
    iconBg: 'bg-kmg-primary/10',
    iconColor: 'text-kmg-primary',
    borderColor: 'border-kmg-primary/20',
  },
  {
    icon: Eye,
    title: 'Client-Facing Solutions',
    description: 'Platforms that transform your clients experience with revolutionary interfaces, brand based on data.',
    iconBg: 'bg-kmg-accent/10',
    iconColor: 'text-kmg-accent',
    borderColor: 'border-kmg-accent/20',
  },
  {
    icon: FlaskConical,
    title: 'Research Projects',
    description: 'Pushing boundaries through experimental work and novel approaches.',
    iconBg: 'bg-purple-500/10',
    iconColor: 'text-purple-600',
    borderColor: 'border-purple-500/20',
  },
  {
    icon: Zap,
    title: 'Data Transformation',
    description: 'Turning existing data into value, aggregating insights, and creating entirely new data streams.',
    iconBg: 'bg-emerald-500/10',
    iconColor: 'text-emerald-600',
    borderColor: 'border-emerald-500/20',
  },
];

const imageSlides = [
  {
    titlePrefix: 'Mobile pet grooming turned',
    logo: '/clients/mobi.png',
    logoAlt: 'Mobispaw',
    imageSrc: '/aboutUs/mobispaw.png',
    imageAlt: 'Mobispaw systems',
    summary: 'Built internal operations and a client booking system that streamlines scheduling, payments, and pet records.',
  },
  {
    titlePrefix: 'Sporting company turned',
    logo: '/clients/spike.png',
    logoAlt: 'Spikeball',
    imageSrc: '/aboutUs/spikeball.png',
    imageAlt: 'Spikeball systems',
    summary: 'Built the community app and data pipeline that powers events, engagement, and actionable insights.',
  },
  {
    titlePrefix: 'Maintenance management turned',
    logo: '/clients/TNF.png',
    logoAlt: 'The National Fix',
    imageSrc: '/aboutUs/theNationalFix.png',
    imageAlt: 'The National Fix systems',
    summary: 'Built a centralized operations platform to manage facilities, tickets, and service workflows at scale.',
  },
  {
    titlePrefix: 'Real estate management turned',
    logo: '/clients/priveda.png',
    logoAlt: 'Priveda Capital',
    imageSrc: '/aboutUs/priveda.png',
    imageAlt: 'Priveda Capital systems',
    summary: 'Built portfolio and reporting systems that keep assets, tenants, and performance in one view.',
  },
  {
    titlePrefix: 'Wealth management turned',
    logo: '/clients/vcCircle.png',
    logoAlt: 'Viridia Capital',
    imageSrc: '/aboutUs/viridia.png',
    imageAlt: 'Viridia Capital systems',
    summary: 'Built secure client reporting and analytics to turn complex data into clear decisions.',
  },
  {
    titlePrefix: 'Test and balance company turned',
    logo: '/clients/smarttab.png',
    logoAlt: 'smarTAB',
    imageSrc: '/aboutUs/smartab.png',
    imageAlt: 'smarTAB systems',
    summary: 'Built systems to manage testing, reporting, and performance data across facilities.',
  },
  {
    titlePrefix: 'Plumbing services turned',
    logo: '/clients/levine.png',
    logoAlt: 'Levine Bros',
    imageSrc: '/aboutUs/levineBros.png',
    imageAlt: 'Levine Bros systems',
    summary: 'Built service tracking and customer systems that modernize day‑to‑day operations.',
  },
];

const Product = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const [highlightIndex, setHighlightIndex] = useState(0);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setHighlightIndex((prev) => (prev + 1) % imageSlides.length);
    }, 5200);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    
    // Skip ScrollTrigger animations on mobile for better performance
    if (isMobile) {
      // Just set elements to visible state immediately on mobile
      if (titleRef.current) {
        gsap.set(titleRef.current, { opacity: 1, y: 0 });
      }
      if (contentRef.current) {
        gsap.set(contentRef.current, { opacity: 1, x: 0 });
      }
      const featureCards = featuresRef.current?.querySelectorAll('.feature-card');
      if (featureCards) {
        gsap.set(featureCards, { opacity: 1, y: 0 });
      }
      return;
    }
    
    const ctx = gsap.context(() => {
      // Set initial states first to prevent flashing
      if (titleRef.current) gsap.set(titleRef.current, { opacity: 1, y: 0 });
      if (contentRef.current) gsap.set(contentRef.current, { opacity: 1, x: 0 });
      
      // Title animation - desktop only
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'expo.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 80%',
            once: true, // Only animate once - prevents flashing
            toggleActions: 'play none none none', // Never reverse
          },
        }
      );

      // Content animation - desktop only
      gsap.fromTo(
        contentRef.current,
        { opacity: 0, x: -50 },
        {
          opacity: 1,
          x: 0,
          duration: 0.7,
          ease: 'expo.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            once: true, // Only animate once - prevents flashing
            toggleActions: 'play none none none', // Never reverse
          },
        }
      );

      // Features animation - desktop only
      const featureCards = featuresRef.current?.querySelectorAll('.feature-card');
      if (featureCards) {
        // Set initial state first
        gsap.set(featureCards, { opacity: 1, y: 0 });
        
        gsap.fromTo(
          featureCards,
          { opacity: 0, y: 60 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.1,
            ease: 'expo.out',
            scrollTrigger: {
              trigger: featuresRef.current,
              start: 'top 90%',
              once: true, // Only animate once - prevents flashing
              toggleActions: 'play none none none', // Never reverse
            },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="product"
      ref={sectionRef}
      className="relative py-32 bg-kmg-bg overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
        <div className="absolute top-1/3 right-0 w-96 h-96 bg-kmg-primary/5 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div ref={titleRef} className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Look at your company through a <span className="text-gradient">tech lens</span>
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed">
            Regardless of industry, scaling starts the same way. A tech-first approach builds the foundation that everything else scales on.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-12">
          {/* Content */}
          <div ref={contentRef} className="space-y-8">
            <div>
              <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4 block">
                Platform
              </span>
              <div className="flex items-center gap-4 mb-8">
                <img
                  src={imageSlides[highlightIndex].logo}
                  alt={imageSlides[highlightIndex].logoAlt}
                  className="w-10 h-10 object-contain"
                />
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-[1.1]">
                  {imageSlides[highlightIndex].titlePrefix}{' '}
                  <span className="text-gradient">tech</span>
                </h3>
              </div>
            </div>
            
            <div className="space-y-5 border-t border-slate-200 pt-6">
              <p className="text-lg sm:text-xl text-slate-700 leading-relaxed font-medium">
                {imageSlides[highlightIndex].summary}
              </p>
            </div>
          </div>

        {/* Client Cards */}
        <div className="relative w-full h-[360px] sm:h-[420px] flex items-center justify-center">
          {imageSlides.map((slide, index) => {
            const isActive = index === highlightIndex;
            return (
              <div
                key={slide.logo}
                className={`absolute inset-0 flex items-center justify-center transition-all duration-700 ease-out ${
                  isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'
                }`}
              >
                <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-4 sm:p-6 w-full max-w-lg">
                  <div className="aspect-[16/10] w-full overflow-hidden rounded-2xl bg-transparent flex items-center justify-center">
                    <img
                      src={slide.imageSrc}
                      alt={slide.imageAlt}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </div>

        {/* Capabilities Grid */}
        <div ref={featuresRef} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {capabilities.map((capability, index) => {
            const Icon = capability.icon;
            return (
              <div
                key={index}
                className="feature-card group relative p-6 bg-white rounded-xl shadow-card hover:shadow-card-hover transition-all duration-300 border border-slate-100 hover:border-slate-200"
              >
                <div className={`w-12 h-12 flex items-center justify-center ${capability.iconBg} rounded-lg mb-4 transition-all duration-300 group-hover:scale-105 group-hover:shadow-sm`}>
                  <Icon className={`w-6 h-6 ${capability.iconColor} transition-transform duration-300 group-hover:scale-110`} />
                </div>
                <h4 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-slate-800 transition-colors">
                  {capability.title}
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {capability.description}
                </p>
              </div>
            );
          })}
        </div>
        <p className="text-base sm:text-lg text-slate-600 leading-relaxed mt-8 text-center max-w-4xl mx-auto">
          An unconventional engineering approach to solving challenges, combined with staying ahead of emerging technologies, makes it possible for your business to scale rapidly.
        </p>
      </div>
    </section>
  );
};

export default Product;
